import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Card from 'reactstrap/lib/Card';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import {
  getAppraisalLink,
  getAppraisalLinkTextFromVehicle,
} from 'site-modules/shared/utils/appraisal-link-constructor';
import { CORE_GALLERY_SIZE } from 'site-modules/core-page/utils/constants';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import { Link } from 'site-modules/shared/components/link/link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { BuildPriceButtonAd } from 'site-modules/shared/components/native-ad/build-price-button-ad/build-price-button-ad';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { InternationalZipPicker } from 'site-modules/shared/components/international-zip-picker/international-zip-picker';
import { EditorsChoiceBadge } from 'site-modules/shared/components/editors-choice-badge/editors-choice-badge';
import { PhotoGallery } from 'site-modules/core-page/components/photo-gallery/photo-gallery';
import { CoreHighlights } from 'site-modules/core-page/components/core-highlights/core-highlights';
import { CorePricingHistogram } from 'site-modules/core-page/components/core-pricing-histogram/core-pricing-histogram';
import { YearSubmodelControls } from 'site-modules/core-page/components/year-submodel-controls/year-submodel-controls';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

import { ListPriceRange } from './list-price-range';
import './used-core-intro.scss';

export function UsedCoreIntro({
  params,
  vehicle,
  editorialReview = null,
  isInternational = false,
  isMobile = false,
  isCore7646Enabled = false,
}) {
  const { isNoSubmodelPage } = params;
  const {
    year,
    make: { slug: makeSlug },
    model: { slug: modelSlug },
    submodels: { slug: submodelSlug },
  } = vehicle;

  const displayVehicle = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    trimDuplicateSubmodel: true,
    updateChevroletMakeName: true,
    disableTrademarkCharacter: true,
    usedPrefix: isNoSubmodelPage ? '' : 'Used',
  });
  const title = isNoSubmodelPage ? `${displayVehicle} Review` : displayVehicle;

  const yearSubmodelControls = (
    <YearSubmodelControls params={params} vehicle={vehicle} isUsed isCore7646Enabled={isCore7646Enabled} />
  );

  const pricingHistogramFragment = (
    <Fragment>
      <h2 className="heading-4 mb-1">Price & Payment</h2>
      <div className="bg-white rounded-8 px-1 py-1 mb-1_5">
        {yearSubmodelControls}
        <CorePricingHistogram />
      </div>
      <Button
        tag={Link}
        to={getAppraisalLink({ make: makeSlug, model: modelSlug, year })}
        data-tracking-id="tool_select_appraise_your_car"
        size="lg"
        color="outline-blue-50"
        className="cta-button rounded-8 w-100 py-0_75 text-transform-none btn-bg-white"
      >
        {isNoSubmodelPage && !isMobile ? getAppraisalLinkTextFromVehicle(vehicle) : 'Appraise This Car'}
      </Button>
    </Fragment>
  );

  return (
    <Container className="used-core-intro">
      <h1 className="intro-title heading-2 text-white mb-1">{title}</h1>
      <ListPriceRange
        className="text-white mb-1"
        valueClassName="d-inline-block fw-bold size-20"
        params={params}
        submodelSlug={submodelSlug}
        isUnknownZip={isInternational}
      />

      <Row className="gx-1_25 gy-1_5 mb-2">
        <Col xs={12} lg={8} className="d-flex flex-column gap-1">
          <div className="intro-gallery pos-r">
            <PhotoGallery
              params={params}
              isMobile={isMobile}
              vehicle={vehicle}
              editorialReview={editorialReview}
              photoSize={isMobile ? CORE_GALLERY_SIZE.MOBILE.HERO : CORE_GALLERY_SIZE.DESKTOP.HERO}
              isUsed
              hideGalleryRow={isMobile}
            />
            {isMobile && <EditorsChoiceBadge vehicleParams={params} className="m-0_5" />}
          </div>
          <div className="intro-highlights rounded-12 bg-cool-gray-90 px-1 py-1">
            <CoreHighlights
              params={params}
              vehicle={vehicle}
              editorialReview={editorialReview}
              isMobile={isMobile}
              isUsed
            />
            {!isMobile && (
              <IncentivesAd
                wrapperClass="mt-1_5"
                cardWrapper="rounded-12 bg-cool-gray-90"
                horizontalLayout
                showOnlySiteServed
              />
            )}
          </div>
        </Col>
        <Col xs={12} lg={4}>
          {!isMobile && (
            <ScrollElement
              id={MENU_HASH.PRICE_HISTOGRAM}
              tag={Card}
              className="intro-trims-content rounded-12 bg-cool-gray-90 px-1 py-1 px-lg-0_5 px-xl-1 m-lg-0"
              data-tracking-parent="edm-entry-model-summary"
            >
              {isInternational ? (
                <Experiment name="core-6829-used-nozip" showDefault>
                  <Recipe name="ctrl" isDefault>
                    {yearSubmodelControls}
                    <InternationalZipPicker
                      btnTitle="Go"
                      isFullWidth
                      className="border-0"
                      btnClassName="btn-blue-50 size-16"
                      colConfig={{ zip: { xs: 4 }, btn: { xs: 4 } }}
                    />
                  </Recipe>
                  <Recipe name="chal">{pricingHistogramFragment}</Recipe>
                </Experiment>
              ) : (
                pricingHistogramFragment
              )}
              <BuildPriceButtonAd position={'1'} wrapperClass="mt-1_5 pt-1_5" linkUrlClass="text-blue-40" />
            </ScrollElement>
          )}
          {isMobile && <IncentivesAd wrapperClass="px-0" mobile />}
        </Col>
      </Row>
    </Container>
  );
}

UsedCoreIntro.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  editorialReview: EditorialReviewEntities.EditorialReview,
  isInternational: PropTypes.bool,
  isMobile: PropTypes.bool,
  isCore7646Enabled: PropTypes.bool,
};
